import { useState, useEffect, useRef } from 'react';

interface TypedSpeeds {
  type:   number;
  delete: number;
  pause:  number;
}

const DEFAULT_SPEEDS: TypedSpeeds = {
  type:   40,
  delete: 20,
  pause:  2400,
};

export function useTyped(phrases: string[], speeds: TypedSpeeds = DEFAULT_SPEEDS): string {
  const [text, setText] = useState('');
  const state = useRef({ phraseIndex: 0, charIndex: 0, deleting: false });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (phrases.length === 0) return;

    function tick(): void {
      const { phraseIndex, charIndex, deleting } = state.current;
      const phrase = phrases[phraseIndex] ?? '';

      if (!deleting) {
        const next = charIndex + 1;
        setText(phrase.slice(0, next));
        state.current.charIndex = next;

        if (next === phrase.length) {
          state.current.deleting = true;
          timerRef.current = setTimeout(tick, speeds.pause);
          return;
        }
        timerRef.current = setTimeout(tick, speeds.type);
      } else {
        const next = charIndex - 1;
        setText(phrase.slice(0, next));
        state.current.charIndex = next;

        if (next === 0) {
          state.current.deleting = false;
          state.current.phraseIndex = (phraseIndex + 1) % phrases.length;
          timerRef.current = setTimeout(tick, 350);
          return;
        }
        timerRef.current = setTimeout(tick, speeds.delete);
      }
    }

    timerRef.current = setTimeout(tick, speeds.type);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [phrases, speeds.type, speeds.delete, speeds.pause]);

  return text;
}
